// A long table drawn virtually (only the rows in view exist in the page), with sticky headers and rows that
// size to their content. For lists that can run to thousands of rows.
import { useRef, type ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

export interface VtColumn<T> {
  key: string
  label: ReactNode
  /** CSS grid track, e.g. '90px' or 'minmax(160px, 2fr)'. */
  width: string
  align?: 'right' | 'center'
  render: (row: T, index: number) => ReactNode
}

export function VirtualTable<T>({
  rows,
  columns,
  rowKey,
  estimate = 48,
  height = 'min(68vh, calc(100vh - 340px))',
  onRowClick,
  selected,
  rowTone,
  empty,
}: {
  rows: T[]
  columns: VtColumn<T>[]
  rowKey: (row: T) => string | number
  estimate?: number
  height?: string
  onRowClick?: (row: T) => void
  selected?: (row: T) => boolean
  rowTone?: (row: T) => 'group' | 'muted' | undefined
  empty?: ReactNode
}) {
  const parent = useRef<HTMLDivElement>(null)
  const v = useVirtualizer({ count: rows.length, getScrollElement: () => parent.current, estimateSize: () => estimate, overscan: 10, getItemKey: (i) => rowKey(rows[i]) })
  const template = columns.map((c) => c.width).join(' ')
  // Narrow screens scroll the whole table sideways (header and rows together) rather than squashing it.
  const minWidth = columns.reduce((s, c) => s + (Number(/(\d+)px/.exec(c.width)?.[1]) || 60), 0) + 12 * (columns.length - 1) + 32
  return (
    <div className="vt" role="table" aria-rowcount={rows.length}>
      <div className="vt-head" role="row" style={{ gridTemplateColumns: template, minWidth }}>
        {columns.map((c) => (
          <div key={c.key} role="columnheader" style={{ textAlign: c.align }}>
            {c.label}
          </div>
        ))}
      </div>
      <div ref={parent} className="vt-body" style={{ height, minWidth }}>
        {!rows.length && empty ? <div className="vt-empty">{empty}</div> : null}
        <div style={{ height: v.getTotalSize(), position: 'relative' }}>
          {v.getVirtualItems().map((item) => {
            const row = rows[item.index]
            const tone = rowTone?.(row)
            return (
              <div
                key={item.key}
                ref={v.measureElement}
                data-index={item.index}
                role="row"
                className={`vt-row${onRowClick ? ' clickable' : ''}${selected?.(row) ? ' on' : ''}${tone ? ` ${tone}` : ''}`}
                style={{ gridTemplateColumns: template, transform: `translateY(${item.start}px)` }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <div key={c.key} role="cell" style={{ textAlign: c.align }}>
                    {c.render(row, item.index)}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
